import {
  isMcpAuthorizationPageError,
  MCP_AUTHORIZATION_ERROR_MESSAGES,
  MCP_CONNECTION_MAX_AGE_DAYS,
  type McpAuthorizationDecision,
  type McpAuthorizationRequest,
} from '@easyhelp/shared';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useSearchParams } from 'react-router';
import { browserNavigation, useDecideMcpAuthorization, useMcpAuthorizationRequest } from '../../api/mcp-oauth';
import { PRIMARY_BUTTON_SX, SECONDARY_BUTTON_SX, TARGET_SX } from '../../components/accessible-buttons';
import { McpAuthorizationLayout } from './McpAuthorizationLayout';

/** Nome del client da mostrare: quello dichiarato alla registrazione, o uno generico. */
function clientLabel(name: string | null): string {
  return name ?? 'Un client MCP';
}

const TIME = new Intl.DateTimeFormat('it-IT', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Rome' });

/**
 * Schermata di consenso dell'MCP (DEC-015 decisione 3, story #151, PAGE-011): il client MCP ha
 * avviato l'autorizzazione e il membro collegato decide se consentirgli di agire a suo nome. Sta
 * dietro `RequireSession`: senza sessione si passa dal login Google e si torna qui.
 */
export function McpConsentPage() {
  const [params] = useSearchParams();
  const requestId = params.get('richiesta') ?? '';
  const request = useMcpAuthorizationRequest(requestId);
  const decide = useDecideMcpAuthorization(requestId);

  if (requestId === '' || (request.isError && request.error.code === 'mcp.authorization_not_found')) {
    return (
      <McpAuthorizationLayout title="Collegamento non riuscito">
        <Alert severity="error">{MCP_AUTHORIZATION_ERROR_MESSAGES['mcp.authorization_not_found']}</Alert>
      </McpAuthorizationLayout>
    );
  }

  if (request.isError) {
    return (
      <McpAuthorizationLayout title="Collegamento non riuscito">
        <Alert
          severity="error"
          action={
            <Button color="inherit" onClick={() => void request.refetch()} disabled={request.isFetching} sx={TARGET_SX}>
              Riprova
            </Button>
          }
        >
          <AlertTitle>Impossibile leggere la richiesta di collegamento</AlertTitle>
          {request.error.message}
        </Alert>
      </McpAuthorizationLayout>
    );
  }

  if (request.isPending) {
    return (
      <McpAuthorizationLayout title="Collegamento a EasyHelp">
        <Stack direction="row" spacing={2} alignItems="center" role="status">
          <CircularProgress size={20} />
          <Typography>Lettura della richiesta di collegamento…</Typography>
        </Stack>
      </McpAuthorizationLayout>
    );
  }

  const onDecide = (decision: McpAuthorizationDecision) =>
    decide.mutate(decision, { onSuccess: ({ redirectTo }) => browserNavigation.assign(redirectTo) });

  return <ConsentView request={request.data} decide={onDecide} decision={decide} />;
}

interface ConsentViewProps {
  request: McpAuthorizationRequest;
  decide: (decision: McpAuthorizationDecision) => void;
  decision: ReturnType<typeof useDecideMcpAuthorization>;
}

function ConsentView({ request, decide, decision }: ConsentViewProps) {
  const name = clientLabel(request.client.name);
  const { redirectKind, redirectTarget } = request.client;
  const done = decision.isSuccess;
  // Una richiesta scaduta o già usata non si decide più: si riparte dal client.
  const expired = decision.isError && decision.error.code === 'mcp.authorization_not_found';
  const decisionError = decision.isError
    ? isMcpAuthorizationPageError(decision.error.code)
      ? MCP_AUTHORIZATION_ERROR_MESSAGES[decision.error.code]
      : decision.error.message
    : null;

  return (
    <McpAuthorizationLayout title={`Collegare ${name} a EasyHelp`}>
      <Typography>
        <strong>{name}</strong> vuole accedere a EasyHelp come <strong>{request.member.name}</strong> (
        {request.member.email}).
        {request.client.name !== null && ' Il nome è quello dichiarato dal client.'}
      </Typography>

      <Box component="section" aria-labelledby="mcp-cosa-potra-fare">
        <Typography id="mcp-cosa-potra-fare" variant="h6" component="h2" gutterBottom>
          Cosa potrà fare
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: 3, '& li + li': { mt: 1 } }}>
          <li>
            <Typography>Leggere coda, ticket, thread, contesto e allegati, con i permessi del tuo ruolo.</Typography>
          </li>
          <li>
            <Typography>
              Scrivere a tuo nome note interne, priorità, stato, responsabile e correzione: nella cronologia compaiono
              con «via MCP».
            </Typography>
          </li>
          <li>
            <Typography>
              Non può inviare email né risolvere, chiudere o scartare ticket: quelle azioni restano nella console.
            </Typography>
          </li>
        </Box>
      </Box>

      <Typography>
        Ciò che {name} legge, allegati compresi, arriva al fornitore del modello: collega solo i client con l'account
        aziendale. Il collegamento dura al massimo {MCP_CONNECTION_MAX_AGE_DAYS} giorni; «Chiudi le sessioni» in
        Impostazioni › Team lo chiude subito.
      </Typography>

      {redirectKind === 'locale' ? (
        <Alert severity="warning">
          <AlertTitle>Programma su questo computer</AlertTitle>
          Il client riceve la risposta su {redirectTarget}. Consenti solo se hai appena avviato tu il collegamento da
          Claude Code o da Cursor.
        </Alert>
      ) : (
        <Alert severity="warning">
          <AlertTitle>Consenti solo se l'hai avviato tu</AlertTitle>
          {redirectKind === 'app'
            ? `Dopo la scelta il browser apre l'applicazione ${redirectTarget}. `
            : `Dopo la scelta torni a ${redirectTarget}. `}
          Se non hai appena collegato EasyHelp da Claude o da Cursor, premi «Non consentire».
        </Alert>
      )}

      <Typography>Hai tempo fino alle {TIME.format(new Date(request.expiresAt))} per scegliere.</Typography>

      {decisionError !== null && <Alert severity="error">{decisionError}</Alert>}

      {expired ? null : done ? (
        <Stack direction="row" spacing={2} alignItems="center" role="status">
          <CircularProgress size={20} />
          <Typography>Ritorno a {redirectTarget}… Se non succede nulla, torna al client.</Typography>
        </Stack>
      ) : (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <Button
            variant="contained"
            size="large"
            onClick={() => decide('consenti')}
            disabled={decision.isPending}
            sx={PRIMARY_BUTTON_SX}
          >
            Consenti
          </Button>
          <Button
            variant="outlined"
            size="large"
            onClick={() => decide('nega')}
            disabled={decision.isPending}
            sx={SECONDARY_BUTTON_SX}
          >
            Non consentire
          </Button>
        </Stack>
      )}
    </McpAuthorizationLayout>
  );
}
